'use client';

import { useState } from 'react';
import RosterUploader from '@/components/RosterUploader';
import NameSelector from '@/components/NameSelector';
import ShiftTable from '@/components/ShiftTable';
import ShiftTimeConfig from '@/components/ShiftTimeConfig';
import IcsExporter from '@/components/IcsExporter';
import { ParsedRoster, Shift, ShiftTimeMapping } from '@/lib/types';
import { defaultCaseyMappings } from '@/lib/shift-mappings';

type Step = 'upload' | 'select-name' | 'review' | 'export';

const STEP_LABELS: Record<Step, string> = {
  'upload': 'Upload',
  'select-name': 'Select Name',
  'review': 'Review',
  'export': 'Export',
};

export default function Home() {
  const [step, setStep] = useState<Step>('upload');
  const [roster, setRoster] = useState<ParsedRoster | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [customMappings, setCustomMappings] = useState<ShiftTimeMapping[]>([]);

  const handleParsed = (parsed: ParsedRoster) => {
    setRoster(parsed);
    setCustomMappings([]);
    setStep('select-name');
  };

  const handleNameSelect = (name: string) => {
    setSelectedName(name);
    if (roster) {
      setShifts(roster.shiftsByName.get(name) || []);
    }
    setStep('review');
  };

  const unknownCodes = roster?.format === 'casey'
    ? shifts
        .filter(s => s.type === 'other' || (s.type === 'shift' && !s.startTime))
        .map(s => s.label)
        .filter((v, i, a) => a.indexOf(v) === i)
        .filter(code => !defaultCaseyMappings.some(m => m.code === code))
        .filter(code => !customMappings.some(m => m.code === code))
    : [];

  const handleReset = () => {
    setStep('upload');
    setRoster(null);
    setSelectedName('');
    setShifts([]);
    setCustomMappings([]);
  };

  const stepIndex = (s: Step) => (['upload', 'select-name', 'review', 'export'] as Step[]).indexOf(s);

  return (
    <div className="flex flex-col flex-1 items-center justify-center bg-zinc-50 dark:bg-black font-sans">
      <main className="flex flex-col flex-1 w-full max-w-3xl items-center py-16 px-6">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-2">
          Roster Convert
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400 mb-8 text-center">
          Upload your roster, pick your name, export shifts to your calendar.
        </p>

        <div className="flex items-center gap-2 mb-8 text-sm">
          {(Object.keys(STEP_LABELS) as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={i <= stepIndex(step) ? 'font-medium text-zinc-900 dark:text-zinc-100' : 'text-zinc-400'}>
                {STEP_LABELS[s]}
              </span>
              {i < 3 && <span className="text-zinc-300">→</span>}
            </div>
          ))}
        </div>

        <div className="w-full flex flex-col items-center gap-6">
          {step === 'upload' && <RosterUploader />}

          {step === 'select-name' && roster && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Found {roster.names.length} staff — {roster.format === 'mmc' ? 'MMC' : 'Casey'} format.
              </p>
              <NameSelector names={roster.names} onSelect={handleNameSelect} />
              <button
                onClick={handleReset}
                className="text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
              >
                ← Upload different file
              </button>
            </>
          )}

          {step === 'review' && roster && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Shifts for <strong>{selectedName}</strong>
              </p>
              {shifts.length === 0 ? (
                <p className="text-zinc-500">No shifts found.</p>
              ) : (
                <ShiftTable shifts={shifts} />
              )}
              {unknownCodes.length > 0 && (
                <ShiftTimeConfig
                  unknownCodes={unknownCodes}
                  customMappings={customMappings}
                  onUpdateMappings={setCustomMappings}
                />
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select-name')}
                  className="px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-md text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  ← Pick different name
                </button>
                <button
                  onClick={() => setStep('export')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700"
                >
                  Continue to export →
                </button>
              </div>
            </>
          )}

          {step === 'export' && roster && (
            <>
              <IcsExporter
                shifts={shifts}
                userName={selectedName}
                format={roster.format as 'mmc' | 'casey'}
                customMappings={customMappings}
              />
              <button
                onClick={() => setStep('review')}
                className="text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
              >
                ← Back to review
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}